import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormBuilder, FormArray, FormGroup, NgControl } from '@angular/forms';
import { Directive, forwardRef, Input, OnInit, Optional, Self } from '@angular/core';
import { AgGridEvent, ModelUpdatedEvent } from 'ag-grid-community';
import { AbstractSubscriptionManager } from '@app/shared/classes';
import { getGridValue } from '@app/shared/utils';
import { AgGridAngular } from 'ag-grid-angular';
import { filter, first } from 'rxjs/operators';

@Directive({
  selector: 'ag-grid-angular[iprightsAgGridFormArray]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AgGridFormArrayDirective),
      multi: true
    }
  ]
})
export class AgGridFormArrayDirective<T> extends AbstractSubscriptionManager implements OnInit, ControlValueAccessor {
  /**
   * @description this array manages the state of the whole grid
   *
   * here we do have a FormGroup for each row with FormControl for each field
   *
   * Because of big amount of possible sensitive cases
   * we should make it possible to get and set an array from outside
   */
  @Input() iprightsAgGridFormArray: FormArray;

  /**
   * @description just a callback for implementation of control value accessor
   */
  private _onChange: (value: T[]) => void;

  constructor(
    private readonly _gridReference: AgGridAngular,
    private readonly _formBuilder: FormBuilder,
    @Self() @Optional() private readonly _ngc: NgControl
  ) {
    super();

    this.iprightsAgGridFormArray = _formBuilder.array([]);
  }

  ngOnInit(): void {
    this._addSubscriptions(
      this._gridReference.gridReady.pipe(first()).subscribe((evt) => this._updateFormArray(evt)),

      this._gridReference.rowDataChanged.subscribe(({ api }) => {
        this._updateGridFormValue(api);
      }),

      // implementation of control value accessor:
      // we have to call passed function on each change of the value
      this.iprightsAgGridFormArray.valueChanges.pipe(filter(() => !!this._onChange)).subscribe((value) => {
        this._onChange(value);

        if (!!this._ngc) {
          this._ngc.valueAccessor.writeValue(value);
        }
      }),

      this._gridReference.modelUpdated.subscribe((evt: ModelUpdatedEvent) => this._updateFormArray(evt))
    );
  }

  /**
   * @description implementation of ControlValueAccessor interface
   * We're simply calling the _onChange function on each change of the form array,
   * so simply assigning this function to _onChange property
   */
  registerOnChange(fn: (value: T[]) => void): void {
    this._onChange = fn;
  }

  // TODO artivanov: implement syncing up of the data from control to the grid data
  /**
   * @description implementation of ControlValueAccessor interface
   * Here we do have to set the value
   */
  writeValue(value: T[]): void {}

  /**
   * @description implementation of ControlValueAccessor interface
   * Currently, we don't need to manage touched state, so we don't have to do anything here
   */
  registerOnTouched(fn: any): void {}

  /**
   * @description implementation of ControlValueAccessor interface
   * Currently, we don't need to manage disabled state, so we don't have to do anything here
   */
  setDisabledState(): void {}

  /**
   * @description this method basically syncing up the value of our grid and value of our form
   */
  private _updateGridFormValue({ api }: AgGridEvent): void {
    const gridValue = getGridValue<T>(api);
    if (!!gridValue) {
      this.iprightsAgGridFormArray?.patchValue(gridValue);
    }
  }

  /**
   * @description this method basically updates the model of our form:
   * for each row we're creating the group with a control for each field
   */
  private _updateFormArray({ api }: AgGridEvent): void {
    if (api?.getRenderedNodes()?.length < 1) {
      return;
    }

    // current value of the grid - list of all rows
    const gridValue: T[] = getGridValue<T>(api);
    // here we're transforming the data from the nodes into the FormGroup configs
    const actualControls: FormGroup[] = gridValue.map((data) => {
      const controlConfigs: { [P in keyof T]?: [any] } = Object.fromEntries(
        // looping trough the object's entries and wrapping the value into array as a object config
        Object.entries(data).map(([key, value]) => [key, [value] as [any]])
      );

      return this._formBuilder.group(controlConfigs);
    });

    this.iprightsAgGridFormArray.controls = actualControls;
    this.iprightsAgGridFormArray?.patchValue(gridValue);

    this._gridReference.gridOptions.api.refreshCells();
  }
}
